import { useWithErrorNotification } from 'shared/ui/GlobalDialog'
import {
  listVanityUrlsAtom,
  allVanityUrlsByProductId,
  useProductForTreeId,
} from './vanityUrls.atoms'
import { useCallback } from 'react'
import {
  createVanityUrl,
  deleteVanityUrl,
  updateVanityUrl,
} from './vanityUrls.utils'
import { useSetAtom, useAtomValue } from 'infrastructure/state/jotai.wrapper'
import { VanityUrl } from './vanityUrls.types'

export function useVanityUrlForProductId() {
  const vanityUrls = useAtomValue(allVanityUrlsByProductId)
  return useCallback(
    (productId: number): VanityUrl | undefined => {
      return vanityUrls?.[productId]
    },
    [vanityUrls],
  )
}

export function useVanityUrlForTreeId() {
  const getProduct = useProductForTreeId()
  const vanityUrls = useAtomValue(allVanityUrlsByProductId)

  return useCallback(
    (treeId: string) => {
      const product = getProduct(treeId)
      if (!product) return
      return {
        vanityId: vanityUrls[product.id].URL,
        productId: product.id,
        productTitle: product.title,
      }
    },
    [vanityUrls, getProduct],
  )
}

export function useUpdateCreateVanityUrl(productId: number) {
  const createFunc = useWithErrorNotification(
    { errorTitle: 'Error Creating Vanity Url' },
    createVanityUrl,
  )
  const updateFunc = useWithErrorNotification(
    { errorTitle: 'Error Updating Vanity Url' },
    updateVanityUrl,
  )
  const refresh = useSetAtom(listVanityUrlsAtom)
  const getVanityUrl = useVanityUrlForProductId()

  return useCallback(
    async (url: string) => {
      const vanityUrl = getVanityUrl(productId)
      if (!vanityUrl) {
        await createFunc(productId, url)
      } else {
        await updateFunc(vanityUrl.ID, url)
      }
      refresh()
    },
    [productId, getVanityUrl, refresh, createFunc, updateFunc],
  )
}

export function useDeleteVanityUrl(productId: number) {
  const deleteFunc = useWithErrorNotification(
    { errorTitle: 'Error Deleting Vanity Url' },
    deleteVanityUrl,
  )
  const refresh = useSetAtom(listVanityUrlsAtom)
  const getVanityUrl = useVanityUrlForProductId()

  return useCallback(async () => {
    const vanityUrl = getVanityUrl(productId)
    if (!vanityUrl) {
      return
    }
    await deleteFunc(vanityUrl.ID)
    refresh()
  }, [productId, getVanityUrl, refresh, deleteFunc])
}
